/**
 * Create or edit a ministry (name, description, active flag).
 */

import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, Group } from '@/components/ui/layout';
import { Button, Text } from '@/components/ui/core';
import { TextInput, Textarea, Switch } from '@/components/ui/inputs';
import { Drawer } from '@/components/ui/overlays';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useMinistryService, type Ministry, type MinistryModelView } from '@actschurch/ccms-sdk';
import { invalidateMinistryQueries, errorMessage, ErrorNotice } from '../../components';

interface MinistryFormDrawerProps {
  opened: boolean;
  onClose: () => void;
  ministry?: Pick<MinistryModelView, 'ministryID' | 'name' | 'description' | 'isActive'> | null;
  onSaved?: (saved: Ministry) => void;
}

export default function MinistryFormDrawer({ opened, onClose, ministry, onSaved }: MinistryFormDrawerProps) {
  const isEditMode = !!ministry;
  const ministryService = useMinistryService();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: { name: '', description: '', isActive: true },
    validate: {
      name: (value) => (!value?.trim() ? 'Name is required' : value.trim().length > 100 ? 'Keep the name under 100 characters' : null),
    },
  });

  useEffect(() => {
    if (!opened) return;
    if (ministry) {
      form.setValues({ name: ministry.name || '', description: ministry.description || '', isActive: ministry.isActive });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, ministry]);

  const save = useMutation({
    mutationFn: (values: typeof form.values) =>
      isEditMode
        ? ministryService.update(ministry!.ministryID, { name: values.name.trim(), description: values.description.trim() || null, isActive: values.isActive })
        : ministryService.create({ name: values.name.trim(), description: values.description.trim() || null }),
    onSuccess: (saved) => {
      invalidateMinistryQueries(queryClient);
      toast.success(isEditMode ? 'Ministry updated' : 'Ministry created', saved.name);
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => toast.error(isEditMode ? 'Could not update the ministry' : 'Could not create the ministry', errorMessage(err)),
  });

  const handleClose = () => {
    if (save.isPending) return;
    save.reset();
    onClose();
  };

  return (
    <Drawer opened={opened} onClose={handleClose} title={isEditMode ? 'Edit ministry' : 'New ministry'} position="right" size="md">
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <Stack gap="md">
          <TextInput label="Name" placeholder="For example Worship" required data-autofocus {...form.getInputProps('name')} />
          <Textarea label="Description" placeholder="What this ministry does" rows={4} {...form.getInputProps('description')} />
          {isEditMode && (
            <Switch
              label="Active"
              description="Inactive ministries are hidden from the public ministries list and pickers. Members stay assigned."
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}
          <ErrorNotice error={save.error} title={isEditMode ? 'Could not update the ministry' : 'Could not create the ministry'} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {isEditMode ? 'Save changes' : 'Create ministry'}
            </Button>
          </Group>
          {!isEditMode && (
            <Text size="xs" c="dimmed">
              You can add members, teams, equipment and activities once the ministry exists.
            </Text>
          )}
        </Stack>
      </form>
    </Drawer>
  );
}
